import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Bell, Target, TrendingUp, AlertTriangle, Zap,
  User, Mail, Calendar, ChevronRight, CheckCircle2, Clock,
  BarChart3, MessageSquare, Eye, Star, FileText, ArrowRight,
  Plus, Trash2, DollarSign, FileCheck, Download, Send,
  StickyNote, Receipt, Award, XCircle, Paperclip, Upload, Shield, Edit2, AlertCircle, BookOpen,
  Camera, Lock, Edit, Loader2, RefreshCw, CheckCircle
} from 'lucide-react';
import CoachOnboardingModal from '@/components/CoachOnboardingModal';
import { CoachSessionsTab, CoachSettingsTab } from '@/components/coach-dashboard';

const API_URL = window.location.origin;

// Note type display formatter
const formatNoteType = (type) => {
  const typeLabels = {
    'general': 'General',
    'session': 'Session Notes',
    'initial_alignment': 'Initial Alignment Meeting',
    'midpoint_alignment': 'Midpoint Alignment Meeting',
    'final_alignment': 'Final Alignment Meeting',
    '360_assessment': '360 Assessment & Feedback',
    'concern': 'Concern',
    'progress': 'Progress Update' // Legacy support
  };
  return typeLabels[type] || type;
};

// Get initial tab from URL path
const getTabFromPath = (pathname) => {
  if (pathname.includes('/notes')) return 'notes';
  if (pathname.includes('/invoices')) return 'invoices';
  if (pathname.includes('/agreements')) return 'agreements';
  if (pathname.includes('/notifications')) return 'notifications';
  if (pathname.includes('/messages')) return 'messages';
  if (pathname.includes('/profile')) return 'profile';
  if (pathname.includes('/sessions')) return 'sessions';
  if (pathname.includes('/settings')) return 'settings';
  return 'clients';
};

// Heat map color helper
const getHeatMapColor = (level) => {
  switch (level) {
    case 'low': return 'bg-red-100 text-red-700 border-red-200';
    case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'high': return 'bg-green-100 text-green-700 border-green-200';
    default: return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const getScoreColor = (score) => {
  if (score <= 4) return 'text-red-600';
  if (score <= 6) return 'text-yellow-600';
  return 'text-green-600';
};

const getTierBadgeColor = (tier) => {
  switch (tier) {
    case 'sdc': return 'bg-purple-100 text-purple-700';
    case 'inner_edge': return 'bg-blue-100 text-blue-700';
    case 'inner_circle': return 'bg-green-100 text-green-700';
    case 'coaching_activation': return 'bg-orange-100 text-orange-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-700';
    case 'approved': return 'bg-blue-100 text-blue-700';
    case 'paid': return 'bg-green-100 text-green-700';
    case 'returned': return 'bg-red-100 text-red-700';
    case 'rescinded': return 'bg-gray-100 text-gray-500';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// Client Card Component
const ClientCard = ({ client, onViewDetails, onAddNote }) => {
  const coachView = client.coach_view || {};
  const scores = coachView.category_scores || {};
  const isHighRisk = coachView.is_high_risk;
  const isHighLeverage = coachView.is_high_leverage;
  const tiers = client.tiers || [];

  return (
    <Card className="card-base hover:border-terracotta/30 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-forest/10 flex items-center justify-center">
              <User className="h-6 w-6 text-forest" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-forest">{client.name || client.user_name}</h3>
              <p className="text-sm text-gray-500">{client.email || client.user_email}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {tiers.map((tier, i) => (
                  <Badge key={i} className={`text-xs ${getTierBadgeColor(tier)}`}>
                    {tier.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            {isHighRisk && (
              <Badge className="bg-red-100 text-red-700">
                <AlertTriangle className="h-3 w-3 mr-1" /> Risk
              </Badge>
            )}
            {isHighLeverage && (
              <Badge className="bg-green-100 text-green-700">
                <Zap className="h-3 w-3 mr-1" /> Leverage
              </Badge>
            )}
          </div>
        </div>

        {/* Quick Scores */}
        {Object.keys(scores).length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            {Object.entries(scores).slice(0, 3).map(([category, score]) => (
              <div key={category} className="text-center p-2 bg-sand/30 rounded-lg">
                <p className={`text-lg font-bold ${getScoreColor(score)}`}>{score}/10</p>
                <p className="text-xs text-gray-500 truncate">{category}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Button 
            size="sm" 
            className="flex-1 btn-secondary"
            onClick={() => onViewDetails(client)}
          >
            <Eye className="h-4 w-4 mr-1" /> View Details
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => onAddNote(client)}
          >
            <StickyNote className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CoachDashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const { userId, partnerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [clients, setClients] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [ratings, setRatings] = useState({ average_rating: 0, total_reviews: 0, reviews: [] });
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showRatingsDialog, setShowRatingsDialog] = useState(false);
  // Coach onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [noteForm, setNoteForm] = useState({ client_id: '', note: '', note_type: 'general' });
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '',
    client_id: '',
    session_type: '',
    session_numbers: '',
    session_dates: '',
    amount: '',
    invoice_link: '',
    notes_completed: false
  });
  // Invoice preview/edit state
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [showEditInvoice, setShowEditInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({
    sessions_count: 1,
    session_dates: '',
    amount: '',
    notes: ''
  });
  const [invoiceSortBy, setInvoiceSortBy] = useState('date'); // 'date', 'status', 'due', 'amount'
  // Journal state
  const [sharedJournalEntries, setSharedJournalEntries] = useState([]);
  const [selectedJournalClient, setSelectedJournalClient] = useState(null);
  const [loadingJournal, setLoadingJournal] = useState(false);
  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  // My Profile state
  const [myProfile, setMyProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [bioForm, setBioForm] = useState({ 
    bio: '', 
    short_bio: '',
    title: '',
    specialties: [],
    credentials: [],
    calendly_url: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const profilePhotoRef = useRef(null);
  
  // Sessions state
  const [clientSessions, setClientSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [completingSession, setCompletingSession] = useState(null);
  
  // Settings state
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    checkOnboardingStatus();
  }, []);

  // Check if coach needs to complete onboarding
  const checkOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/onboarding-status`);
      // Show onboarding if not completed and terms not accepted
      if (!response.data.onboarding_completed || !response.data.terms_accepted) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setCheckingOnboarding(false);
    }
  };

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    if (refreshUser) await refreshUser();
  };

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Fetch conversations when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchConversations();
    }
    if (activeTab === 'profile') {
      fetchMyProfile();
    }
  }, [activeTab]);

  // Fetch messages when a partner is selected
  useEffect(() => {
    if (partnerId && activeTab === 'messages') {
      fetchMessagesWithPartner(partnerId);
      // Find partner info from conversations
      const conv = conversations.find(c => c.partner?.id === partnerId);
      if (conv) {
        setSelectedPartner(conv.partner);
      }
    }
  }, [partnerId, conversations, activeTab]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch sessions when sessions tab is active
  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchClientSessions();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    try {
      const [clientsRes, notificationsRes, notesRes, invoicesRes, agreementsRes, ratingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/coach/all-clients`),
        axios.get(`${API_URL}/api/coach/notifications`),
        axios.get(`${API_URL}/api/coach/notes`),
        axios.get(`${API_URL}/api/coach/invoices`),
        axios.get(`${API_URL}/api/coach/agreements`),
        axios.get(`${API_URL}/api/coach/ratings`)
      ]);
      
      setClients(clientsRes.data || []);
      setNotifications(notificationsRes.data || []);
      setNotes(notesRes.data || []);
      setInvoices(invoicesRes.data || []);
      setAgreements(agreementsRes.data || []);
      setRatings(ratingsRes.data || { average_rating: 0, total_reviews: 0, reviews: [] });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/coach/my-profile`);
      setMyProfile(response.data);
      if (response.data.coach) {
        setBioForm({
          bio: response.data.coach.bio || '',
          short_bio: response.data.coach.short_bio || '',
          title: response.data.coach.title || '',
          specialties: response.data.coach.specialties || [],
          credentials: response.data.coach.credentials || [],
          calendly_url: response.data.coach.calendly_url || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveBio = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/coach/my-profile`, bioForm);
      toast.success('Profile updated successfully');
      setEditingBio(false);
      fetchMyProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setSavingProfile(true);
    try {
      await axios.post(`${API_URL}/api/coach/my-profile/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded successfully');
      fetchMyProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleMarkClientComplete = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/coach/clients/${userId}/complete`);
      toast.success('Client marked as complete');
      fetchMyProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark client complete');
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/messages`);
      setConversations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  // Fetch client sessions for the sessions tab
  const fetchClientSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await axios.get(`${API_URL}/api/coach/client-sessions`);
      setClientSessions(response.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch client sessions:', error);
      toast.error('Failed to load client sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  // Mark a session as complete
  const handleCompleteSession = async (clientId, sessionType) => {
    setCompletingSession(`${clientId}-${sessionType}`);
    try {
      const response = await axios.post(`${API_URL}/api/coach/complete-session`, {
        client_id: clientId,
        session_type: sessionType
      });
      
      toast.success(response.data.message);
      fetchClientSessions(); // Refresh the list
      
      // Optionally create a note for invoicing
      if (response.data.note_created) {
        toast.success('Session note added for invoicing');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    } finally {
      setCompletingSession(null);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/api/account/password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Password changed successfully');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchMessagesWithPartner = async (partnerIdToFetch) => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/messages/${partnerIdToFetch}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !partnerId) return;

    setSendingMessage(true);
    try {
      const response = await axios.post(`${API_URL}/api/coach/messages`, {
        receiver_id: partnerId,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  const fetchClientJournal = async (clientId) => {
    setLoadingJournal(true);
    try {
      const response = await axios.get(`${API_URL}/api/coach/client-journal/${clientId}`);
      setSharedJournalEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch client journal:', error);
      toast.error('Failed to load journal entries');
      setSharedJournalEntries([]);
    } finally {
      setLoadingJournal(false);
    }
  };

  const handleSelectJournalClient = (client) => {
    setSelectedJournalClient(client);
    fetchClientJournal(client.id);
  };

  const handleAddNote = async () => {
    if (!noteForm.client_id || !noteForm.note) {
      toast.error('Please select a client and enter a note');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/coach/notes`, noteForm);
      toast.success('Note added successfully');
      setShowNoteDialog(false);
      setNoteForm({ client_id: '', note: '', note_type: 'general' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/coach/notes/${noteId}`);
      toast.success('Note deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleSubmitInvoice = async () => {
    if (!invoiceForm.invoice_number || !invoiceForm.client_id || !invoiceForm.amount || !invoiceForm.session_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!invoiceForm.invoice_link.trim()) {
      toast.error('Please add your invoice link — this is how your invoice will be paid');
      return;
    }
    
    if (!invoiceFile) {
      toast.error('Please attach your invoice file (PDF, JPG, or PNG)');
      return;
    }
    
    if (!invoiceForm.notes_completed) {
      toast.error('Please confirm you have completed session notes for all billable hours');
      return;
    }
    
    try {
      let invoiceLink = invoiceForm.invoice_link.trim();
      if (!/^https?:\/\//i.test(invoiceLink)) {
        invoiceLink = `https://${invoiceLink}`;
      }
      const payload = {
        ...invoiceForm,
        invoice_link: invoiceLink,
        session_numbers: invoiceForm.session_numbers ? invoiceForm.session_numbers.split(',').map(s => s.trim()).filter(s => s) : [],
        session_dates: invoiceForm.session_dates.split(',').map(d => d.trim()).filter(d => d),
        amount: parseFloat(invoiceForm.amount)
      };
      
      // Create invoice first
      const response = await axios.post(`${API_URL}/api/coach/invoices`, payload);
      const invoiceId = response.data.invoice_id;
      
      // Upload attachment if provided
      if (invoiceFile && invoiceId) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', invoiceFile);
        
        try {
          await axios.post(`${API_URL}/api/coach/invoices/${invoiceId}/attachment`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          toast.error('Invoice created but file upload failed');
        }
        setUploadingFile(false);
      }
      
      toast.success('Invoice submitted successfully');
      setShowInvoiceDialog(false);
      setInvoiceFile(null);
      setInvoiceForm({
        invoice_number: '',
        client_id: '',
        session_type: '',
        session_numbers: '',
        session_dates: '',
        amount: '',
        invoice_link: '',
        notes_completed: false
      });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to submit invoice');
    }
  };

  const handleRescindInvoice = async (invoiceId) => {
    if (!confirm('Rescind this invoice?')) return;
    
    try {
      await axios.put(`${API_URL}/api/coach/invoices/${invoiceId}/rescind`);
      toast.success('Invoice rescinded');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rescind invoice');
    }
  };

  const handlePreviewInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setShowInvoicePreview(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setEditInvoiceForm({
      sessions_count: invoice.sessions_count || 1,
      session_dates: invoice.session_dates?.join(', ') || '',
      amount: invoice.amount || '',
      notes: invoice.notes || ''
    });
    setShowEditInvoice(true);
  };

  const handleSaveInvoiceEdit = async () => {
    if (!selectedInvoice) return;
    
    const needsResubmit = ['returned', 'rescinded'].includes(selectedInvoice.status);
    
    try {
      await axios.put(`${API_URL}/api/coach/invoices/${selectedInvoice.id}/edit`, {
        sessions_count: parseInt(editInvoiceForm.sessions_count),
        session_dates: editInvoiceForm.session_dates,
        amount: parseFloat(editInvoiceForm.amount),
        notes: editInvoiceForm.notes
      });
      
      if (needsResubmit) {
        toast.success('Invoice resubmitted for admin review');
      } else {
        toast.success('Invoice updated successfully');
      }
      setShowEditInvoice(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const getInvoiceDueDate = (createdAt) => {
    const created = new Date(createdAt);
    const due = new Date(created.getTime() + (60 * 24 * 60 * 60 * 1000)); // NET 60
    return due;
  };

  const isInvoiceOverdue = (invoice) => {
    if (invoice.status !== 'pending') return false;
    const dueDate = getInvoiceDueDate(invoice.created_at);
    return new Date() > dueDate;
  };

  // Sort invoices based on selected sort option
  const sortInvoices = (invoiceList) => {
    const statusPriority = { pending: 1, returned: 2, approved: 3, paid: 4, rescinded: 5 };
    
    return [...invoiceList].sort((a, b) => {
      switch (invoiceSortBy) {
        case 'status':
          return (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
        case 'due':
          const dueA = getInvoiceDueDate(a.created_at);
          const dueB = getInvoiceDueDate(b.created_at);
          return dueA - dueB;
        case 'amount':
          return (b.amount || 0) - (a.amount || 0);
        case 'date':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
  };

  const openAddNoteForClient = (client) => {
    setNoteForm({ ...noteForm, client_id: client.id || client.user_id });
    setShowNoteDialog(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
        </div>
      </DashboardLayout>
    );
  }

  const unreadNotifications = notifications.filter(n => !n.read).length;

  // Get contextual page title and description based on active tab
  const getPageInfo = () => {
    switch (activeTab) {
      case 'clients':
        return {
          title: 'Coach Dashboard',
          subtitle: 'Overview of your coaching practice',
          helpText: 'View all your matched clients, their diagnostic scores, and quick access to key metrics. Click on a client card to see their full profile and coaching insights.'
        };
      case 'notes':
        return {
          title: 'Confidential Notes',
          subtitle: 'Private notes visible only to you and administrators',
          helpText: 'Document session insights, client progress, and important observations. Notes are organized by client and can be categorized by type (General, Session Notes, Alignment Meetings, 360 Assessment, Concern).'
        };
      case 'invoices':
        return {
          title: 'Invoice Management',
          subtitle: 'Track and submit invoices for coaching sessions',
          helpText: 'Create invoices with session details and attach supporting documents (PDF, JPG, PNG). Include invoice number, client name, number of sessions, dates, and amount. Admin will review and process payments.'
        };
      case 'agreements':
        return {
          title: 'Coaching Agreements',
          subtitle: 'View and download your coaching contracts',
          helpText: 'Access all your executed coaching agreements. Download copies for your records. New agreements will be added by admin when clients complete their onboarding.'
        };
      case 'messages':
        return {
          title: 'Messages',
          subtitle: 'Communicate with clients and administrators',
          helpText: 'Send and receive messages from your clients and the admin team. Click "New Message" to start a conversation, or select an existing thread from the list.'
        };
      case 'notifications':
        return {
          title: 'Notifications',
          subtitle: 'Stay updated on client activity and requests',
          helpText: 'View alerts for new client matches, session bookings, invoice updates, and important announcements. Unread notifications are highlighted.'
        };
      default:
        return {
          title: 'Coach Dashboard',
          subtitle: 'Manage your coaching practice',
          helpText: ''
        };
    }
  };

  const pageInfo = getPageInfo();

  return (
    <DashboardLayout>
      {/* Coach Onboarding Modal */}
      <CoachOnboardingModal 
        isOpen={showOnboarding} 
        onComplete={handleOnboardingComplete}
      />
      
      <div className="p-6 lg:p-8" data-testid="coach-dashboard-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="font-heading text-3xl font-bold text-forest">{pageInfo.title}</h1>
              <p className="text-gray-600">{pageInfo.subtitle}</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Rating Display - Clickable */}
              <button 
                onClick={() => setShowRatingsDialog(true)}
                className="flex items-center gap-2 bg-yellow-50 px-4 py-2 rounded-lg hover:bg-yellow-100 transition-colors cursor-pointer"
              >
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="font-semibold text-forest">{ratings.average_rating}</span>
                <span className="text-sm text-gray-500">({ratings.total_reviews} reviews)</span>
              </button>
              {unreadNotifications > 0 && (
                <Badge className="bg-terracotta text-white">
                  <Bell className="h-4 w-4 mr-1" /> {unreadNotifications} new
                </Badge>
              )}
            </div>
          </div>
          {/* Help Text */}
          {pageInfo.helpText && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-sm text-blue-800">{pageInfo.helpText}</p>
            </div>
          )}
        </div>

        {/* Stats Overview - Only show on Dashboard/Clients tab */}
        {activeTab === 'clients' && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="card-base">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-forest mx-auto mb-2" />
                <p className="text-2xl font-bold text-forest">{clients.length}</p>
                <p className="text-xs text-gray-500">Total Clients</p>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 text-center">
                <StickyNote className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-forest">{notes.length}</p>
                <p className="text-xs text-gray-500">Notes</p>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 text-center">
                <Receipt className="h-6 w-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-forest">{invoices.filter(i => i.status === 'pending').length}</p>
                <p className="text-xs text-gray-500">Pending Invoices</p>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 text-center">
                <FileCheck className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-forest">{agreements.length}</p>
                <p className="text-xs text-gray-500">Agreements</p>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 text-center">
                <Bell className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-forest">{unreadNotifications}</p>
                <p className="text-xs text-gray-500">Unread</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-sand/50 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="sessions">Sessions</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="agreements">Agreements</TabsTrigger>
            <TabsTrigger value="messages">
              Messages {conversations.filter(c => c.unread_count > 0).length > 0 && 
                <Badge className="ml-1 bg-terracotta text-white text-xs">
                  {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0)}
                </Badge>
              }
            </TabsTrigger>
            <TabsTrigger value="profile" className="text-forest font-medium">
              My Profile
            </TabsTrigger>
            <TabsTrigger value="settings">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <CoachSessionsTab />
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-forest">All Clients</h2>
              <p className="text-sm text-gray-500">Clients from all membership tiers</p>
            </div>
            {clients.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-forest mb-2">No Clients Yet</h3>
                  <p className="text-gray-500">Clients will appear here once they're matched with you.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clients.map((client, index) => (
                  <ClientCard 
                    key={client.id || client.user_id || index} 
                    client={client} 
                    onViewDetails={setSelectedClient}
                    onAddNote={openAddNoteForClient}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-forest">Confidential Notes</h2>
              <Button onClick={() => setShowNoteDialog(true)} className="btn-primary">
                <Plus className="h-4 w-4 mr-2" /> Add Note
              </Button>
            </div>
            <p className="text-sm text-gray-500 mb-2">These notes are only visible to you and administrators.</p>
            
            {/* ICF Guidelines Notice */}
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Important: ICF Guidelines</p>
                  <p className="text-amber-700 mt-1">
                    Please ensure all notes are documented in accordance with ICF (International Coaching Federation) guidelines 
                    and are submitted for invoice documentation purposes only. Notes should not contain clinical diagnoses, 
                    therapy-related content, or protected health information.
                  </p>
                </div>
              </div>
            </div>
            
            {notes.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <StickyNote className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-forest mb-2">No Notes Yet</h3>
                  <p className="text-gray-500">Add confidential notes about your clients.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => (
                  <Card key={note.id} className="card-base">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-forest">{note.client_name}</p>
                          <p className="text-xs text-gray-500">{note.client_email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{formatNoteType(note.note_type)}</Badge>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{note.note}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Journal Tab - Shared entries from clients */}
          <TabsContent value="journal">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Client List */}
              <Card className="card-base lg:col-span-1">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-forest">Select Client</CardTitle>
                </CardHeader>
                <CardContent>
                  {clients.filter(c => c.matched_coach_id).length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No Inner Edge clients assigned</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {clients.filter(c => c.matched_coach_id).map((client) => (
                        <button
                          key={client.id}
                          onClick={() => handleSelectJournalClient(client)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                            selectedJournalClient?.id === client.id 
                              ? 'bg-forest/10 border border-forest' 
                              : 'hover:bg-sand/50'
                          }`}
                        >
                          <div className="h-10 w-10 rounded-full bg-forest/10 flex items-center justify-center">
                            <span className="text-forest font-semibold">{client.full_name?.charAt(0)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-forest truncate">{client.full_name}</p>
                            <p className="text-xs text-gray-500">Inner Edge</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Journal Entries */}
              <Card className="card-base lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    {selectedJournalClient ? `${selectedJournalClient.full_name}'s Shared Journal` : 'Shared Journal Entries'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedJournalClient ? (
                    <div className="text-center py-12">
                      <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Select a client to view their shared journal entries</p>
                    </div>
                  ) : loadingJournal ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-24 bg-sand rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : sharedJournalEntries.length === 0 ? (
                    <div className="text-center py-12">
                      <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">{selectedJournalClient.full_name} hasn't shared any journal entries yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {sharedJournalEntries.map((entry) => (
                        <div key={entry.id} className="p-4 border border-sand rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-forest">{entry.title}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={
                                  entry.entry_type === 'reflection_response' ? 'bg-purple-100 text-purple-700' :
                                  entry.entry_type === 'coaching_note' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }>
                                  {entry.entry_type === 'reflection_response' ? 'Reflection' :
                                   entry.entry_type === 'coaching_note' ? 'Coaching Note' : 'Note'}
                                </Badge>
                                <span className="text-xs text-gray-400">
                                  {new Date(entry.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">{entry.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h2 className="font-heading text-xl font-semibold text-forest">Invoices</h2>
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">Sort:</span>
                <Select value={invoiceSortBy} onValueChange={setInvoiceSortBy}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">Newest First</SelectItem>
                    <SelectItem value="status">By Status</SelectItem>
                    <SelectItem value="due">Due Soonest</SelectItem>
                    <SelectItem value="amount">Highest Amount</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setShowInvoiceDialog(true)} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" /> Create Invoice
                </Button>
              </div>
            </div>
            <Card className="card-base mb-4 bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-800">
                  <strong>Invoice Requirements:</strong> Include Invoice #, # of sessions, dates of sessions, client name. 
                  Payment terms: <strong>NET 60</strong>
                </p>
              </CardContent>
            </Card>
            
            {invoices.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-forest mb-2">No Invoices Yet</h3>
                  <p className="text-gray-500">Create invoices for your coaching sessions.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sortInvoices(invoices).map((invoice) => {
                  const overdue = isInvoiceOverdue(invoice);
                  const dueDate = getInvoiceDueDate(invoice.created_at);
                  
                  return (
                    <Card key={invoice.id} className={`card-base ${overdue ? 'border-red-300 bg-red-50/50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-semibold text-forest">Invoice #{invoice.invoice_number}</p>
                              <Badge className={getStatusBadge(invoice.status)}>{invoice.status}</Badge>
                              {overdue && (
                                <Badge className="bg-red-500 text-white flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" /> OVERDUE
                                </Badge>
                              )}
                              {invoice.status === 'returned' && (
                                <Badge className="bg-orange-100 text-orange-700">Needs Revision</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">Client: {invoice.client_name}</p>
                            <p className="text-sm text-gray-600">Sessions: {invoice.sessions_count} | Dates: {invoice.session_dates?.join(', ')}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Created: {new Date(invoice.created_at).toLocaleDateString()} | 
                              Due: {dueDate.toLocaleDateString()} (NET 60)
                            </p>
                            {invoice.attachment_url && (
                              <a 
                                href={`${API_URL}${invoice.attachment_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1"
                              >
                                <Paperclip className="h-3 w-3" /> {invoice.attachment_name || 'View Attachment'}
                              </a>
                            )}
                            {invoice.admin_comments && (
                              <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
                                <strong>Admin Feedback:</strong> {invoice.admin_comments}
                              </div>
                            )}
                            {invoice.payment_details && (
                              <p className="text-sm text-green-600 mt-1">Payment: {invoice.payment_details}</p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-2xl font-bold text-forest">${invoice.amount}</p>
                            <div className="flex flex-col gap-1 mt-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-forest"
                                onClick={() => handlePreviewInvoice(invoice)}
                              >
                                <Eye className="h-4 w-4 mr-1" /> Preview
                              </Button>
                              {['pending', 'returned', 'rescinded'].includes(invoice.status) && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-blue-600 border-blue-300"
                                    onClick={() => handleEditInvoice(invoice)}
                                  >
                                    <Edit2 className="h-4 w-4 mr-1" /> Edit
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-red-600 border-red-300"
                                    onClick={() => handleRescindInvoice(invoice.id)}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" /> Rescind
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Agreements Tab */}
          <TabsContent value="agreements">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-forest">Coaching Agreements</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">View and download your executed coaching agreements.</p>
            
            {agreements.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-forest mb-2">No Agreements Yet</h3>
                  <p className="text-gray-500">Agreements will be added by admin and appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {agreements.map((agreement) => (
                  <Card key={agreement.id} className="card-base">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-forest">{agreement.title}</p>
                          <p className="text-sm text-gray-500">{agreement.description}</p>
                          <Badge className={agreement.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {agreement.status}
                          </Badge>
                        </div>
                        {agreement.file_url && (
                          <a href={agreement.file_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <Download className="h-4 w-4 mr-2" /> Download
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-forest">Notifications</h2>
            </div>
            
            {notifications.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-forest mb-2">No Notifications</h3>
                  <p className="text-gray-500">You're all caught up!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <Card key={notification.id} className={`card-base ${!notification.read ? 'border-terracotta/30 bg-terracotta/5' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Bell className={`h-5 w-5 ${!notification.read ? 'text-terracotta' : 'text-gray-400'}`} />
                        <div className="flex-1">
                          <p className="text-gray-700">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="flex h-[calc(100vh-16rem)] border border-sand rounded-lg overflow-hidden bg-white">
              {/* Conversations List */}
              <div className={`w-full md:w-80 border-r border-sand flex flex-col ${partnerId ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-sand">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-heading text-xl font-semibold text-forest">Messages</h2>
                  </div>
                  {/* New Message Button with Dialog */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="btn-primary w-full" size="sm">
                        <Plus className="h-4 w-4 mr-2" /> New Message
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Start New Conversation</DialogTitle>
                        <DialogDescription>Select a client or admin to message</DialogDescription>
                      </DialogHeader>
                      <div className="py-4 space-y-4">
                        {/* Admin Section */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Contact Admin</p>
                          {conversations.filter(c => c.partner?.type === 'admin').map((conv) => (
                            <Button
                              key={conv.partner?.id}
                              variant="outline"
                              className="w-full justify-start mb-2"
                              onClick={() => navigate(`/coach/messages/${conv.partner?.id}`)}
                            >
                              <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                                <Shield className="h-4 w-4 text-purple-600" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium">{conv.partner?.full_name}</p>
                                <p className="text-xs text-gray-500">Administrator</p>
                              </div>
                            </Button>
                          ))}
                        </div>
                        {/* Clients Section */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Message a Client</p>
                          <div className="max-h-60 overflow-y-auto space-y-2">
                            {clients.map((client) => (
                              <Button
                                key={client.id || client.user_id}
                                variant="outline"
                                className="w-full justify-start"
                                onClick={() => navigate(`/coach/messages/${client.id || client.user_id}`)}
                              >
                                <div className="h-8 w-8 rounded-full bg-forest/10 flex items-center justify-center mr-3">
                                  <User className="h-4 w-4 text-forest" />
                                </div>
                                <div className="text-left">
                                  <p className="font-medium">{client.name || client.user_name}</p>
                                  <p className="text-xs text-gray-500">{client.email || client.user_email}</p>
                                </div>
                              </Button>
                            ))}
                            {clients.length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-4">No clients yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No conversations yet</p>
                      <p className="text-sm text-gray-400 mt-2">Click "New Message" to start</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-sand">
                      {conversations.map((conv) => (
                        <Link 
                          key={conv.partner?.id} 
                          to={`/coach/messages/${conv.partner?.id}`}
                          className={`flex items-center gap-3 p-4 hover:bg-sand/50 transition-colors ${
                            partnerId === conv.partner?.id ? 'bg-sand' : ''
                          }`}
                          data-testid={`coach-conversation-${conv.partner?.id}`}
                        >
                          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                            conv.partner?.type === 'admin' ? 'bg-purple-100' : 'bg-forest/10'
                          }`}>
                            {conv.partner?.type === 'admin' ? (
                              <Shield className="h-6 w-6 text-purple-600" />
                            ) : (
                              <User className="h-6 w-6 text-forest" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium text-forest truncate">{conv.partner?.full_name}</h3>
                              {conv.last_message && (
                                <span className="text-xs text-gray-400">
                                  {formatMessageTime(conv.last_message.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${
                                conv.partner?.type === 'admin' ? 'border-purple-300 text-purple-600' : ''
                              }`}>
                                {conv.partner?.type === 'admin' ? 'Admin' : 'Client'}
                              </Badge>
                              {conv.last_message && (
                                <p className="text-sm text-gray-500 truncate flex-1">{conv.last_message.content}</p>
                              )}
                            </div>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="h-5 w-5 rounded-full bg-terracotta text-white text-xs flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className={`flex-1 flex flex-col bg-cream/50 ${!partnerId ? 'hidden md:flex' : 'flex'}`}>
                {partnerId && selectedPartner ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-sand bg-white flex items-center gap-3">
                      <Link to="/coach/messages" className="md:hidden">
                        <Button variant="ghost" size="icon">
                          <ChevronRight className="h-5 w-5 rotate-180" />
                        </Button>
                      </Link>
                      <div className="h-10 w-10 rounded-full bg-forest/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-forest" />
                      </div>
                      <div>
                        <h2 className="font-medium text-forest">{selectedPartner.full_name}</h2>
                        <p className="text-sm text-gray-500">{selectedPartner.type === 'admin' ? 'Administrator' : 'Client'}</p>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <div className="space-y-4 max-w-3xl mx-auto">
                        {messages.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No messages yet</p>
                            <p className="text-sm text-gray-400">Send a message to start the conversation</p>
                          </div>
                        ) : (
                          messages.map((msg, index) => {
                            const showDate = index === 0 || 
                              formatMessageDate(messages[index - 1].created_at) !== formatMessageDate(msg.created_at);
                            const isSent = msg.sender_id === user?.id;

                            return (
                              <div key={msg.id}>
                                {showDate && (
                                  <div className="text-center my-4">
                                    <span className="text-xs text-gray-400 bg-sand/50 px-3 py-1 rounded-full">
                                      {formatMessageDate(msg.created_at)}
                                    </span>
                                  </div>
                                )}
                                <div className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[70%] rounded-xl px-4 py-2 ${
                                    isSent 
                                      ? 'bg-forest text-white rounded-br-sm' 
                                      : 'bg-white border border-sand text-gray-800 rounded-bl-sm'
                                  }`} data-testid={`coach-message-${msg.id}`}>
                                    <p>{msg.content}</p>
                                    <span className={`text-xs mt-1 block ${isSent ? 'text-white/70' : 'text-gray-500'}`}>
                                      {formatMessageTime(msg.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-sand bg-white">
                      <form onSubmit={handleSendMessage} className="flex gap-3 max-w-3xl mx-auto">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="input-base flex-1"
                          data-testid="coach-message-input"
                        />
                        <Button 
                          type="submit" 
                          className="btn-primary"
                          disabled={!newMessage.trim() || sendingMessage}
                          data-testid="coach-send-message-btn"
                        >
                          <Send className="h-5 w-5" />
                        </Button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="font-heading font-semibold text-forest mb-2">Select a Conversation</h3>
                      <p className="text-gray-500">Choose a client or admin to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* My Profile Tab */}
          <TabsContent value="profile">
            {profileLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-forest" />
              </div>
            ) : myProfile ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Card */}
                <Card className="card-base">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="relative inline-block">
                        <img 
                          src={myProfile.coach?.image_url || '/placeholder-coach.jpg'} 
                          alt={myProfile.coach?.name}
                          className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-gold/20"
                        />
                        {!myProfile.coach?.is_locked && (
                          <button 
                            onClick={() => profilePhotoRef.current?.click()}
                            className="absolute bottom-0 right-0 bg-forest text-white p-2 rounded-full hover:bg-forest/90 transition-colors"
                            disabled={savingProfile}
                          >
                            <Camera className="h-4 w-4" />
                          </button>
                        )}
                        <input 
                          ref={profilePhotoRef}
                          type="file" 
                          accept="image/*"
                          className="hidden"
                          onChange={handlePhotoUpload}
                        />
                      </div>
                      <h3 className="mt-4 font-heading font-semibold text-xl text-forest">{myProfile.coach?.name}</h3>
                      <p className="text-gray-500">{myProfile.coach?.title}</p>
                      
                      {/* Status Badges */}
                      <div className="flex justify-center gap-2 mt-3">
                        {myProfile.coach?.is_published === false && (
                          <Badge className="bg-gray-100 text-gray-600">Unpublished</Badge>
                        )}
                        {myProfile.coach?.is_locked && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Lock className="h-3 w-3 mr-1" /> Profile Locked
                          </Badge>
                        )}
                      </div>
                      
                      {myProfile.coach?.is_locked && (
                        <p className="text-sm text-amber-600 mt-3 bg-amber-50 p-2 rounded">
                          Your profile is locked. Contact admin to request changes.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Bio & Details */}
                <Card className="card-base lg:col-span-2">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-forest">Profile Details</CardTitle>
                    <div className="flex items-center gap-2">
                      {/* BIO Example Download */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const bioExample = `BIO EXAMPLE - Use this as a template for your professional bio

Terry Moore, PCC, EMCC

Terry is passionate about the power of coaching and creating change. She leverages her extensive human resource experience to provide group and individual coaching to help the client gain clarity on their goal(s) and develop a plan for implementation. Terry's coaching style is engaging, warm, goal-focused and honest. Clients find her easy to speak to and they trust and have confidence in her approach due to her strong business acumen.

MY PASSION TO COACH:
• Coached leaders and senior executives in the following industry sectors: Automotive, Banking, Consumer Goods, Energy, Financial Services, Food Service, Higher Education, Insurance, Non-Profits, Pharma, Retail, and Tech.
• Joined Yale School of Management Executive Education Program to coach participants attending the Women's Leadership Program and eMBA and MBA students.

MY CAREER HIGHLIGHTS:
• Coached global managers and executives to stronger performance, better leadership and broader organizational responsibility.
• Enhanced performance improvements for high achievers with her extensive knowledge of corporate and organizational structures.
• Designed and implemented highly effective leadership and management development programs.
• Brings over 25 years of experience leading and providing strategic HR support to senior and mid-level executives in four industries.
• Career includes 17 years in progressive Vice President Roles in business, corporate and field human resources and learning organizations, including being part of the Prudential Chairman's talent strategy team.

MY PROFESSIONAL EXPERIENCE:
• Vice President, Learning, designed and implemented a leadership brand, and served as faculty for Prudential's enterprise-wide management and leadership development programs. Created center of excellence for sales suitability and compliance centralizing 76 field-based operations to one location saving $10 million dollars and exceeding diversity goals.
• Senior Vice President, Human Resources at GE Capital. Facilitated two acquisitions which expanded GE Capital's B to B financial and telecom services solutions business.
• Director, Human Resources for GrandMet /Pillsbury across domestic and international, marketing and finance organizations.

MY CREDENTIALS:
• International Coach Federation, Professional Certified Coach
• Fielding Graduate University, Evidenced Based Coaching Certificate
• London School of Economics, MSc., Industrial Relations and Human Resource Management
• Mount Holyoke College, B.A., Politics`;
                          
                          const blob = new Blob([bioExample], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'BIO_Example_Template.txt';
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success('BIO example downloaded!');
                        }}
                        className="text-forest"
                      >
                        <Download className="h-4 w-4 mr-1" /> BIO Example
                      </Button>
                      {!myProfile.coach?.is_locked && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setEditingBio(!editingBio)}
                        >
                          {editingBio ? 'Cancel' : <><Edit className="h-4 w-4 mr-1" /> Edit Profile</>}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {editingBio ? (
                      <div className="space-y-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Professional Title</Label>
                            <Input 
                              value={bioForm.title}
                              onChange={(e) => setBioForm(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="e.g., Executive Coach, PCC"
                            />
                          </div>
                          <div>
                            <Label>Calendly URL</Label>
                            <Input 
                              value={bioForm.calendly_url}
                              onChange={(e) => setBioForm(prev => ({ ...prev, calendly_url: e.target.value }))}
                              placeholder="https://calendly.com/your-link"
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Short Bio (1-2 sentences for display)</Label>
                          <Input 
                            value={bioForm.short_bio}
                            onChange={(e) => setBioForm(prev => ({ ...prev, short_bio: e.target.value }))}
                            placeholder="Brief introduction about yourself"
                          />
                        </div>
                        
                        <div>
                          <Label>Full Bio</Label>
                          <p className="text-xs text-gray-500 mb-1">
                            Download the BIO Example above for a template. Include your passion to coach, career highlights, professional experience, and credentials.
                          </p>
                          <Textarea 
                            value={bioForm.bio}
                            onChange={(e) => setBioForm(prev => ({ ...prev, bio: e.target.value }))}
                            placeholder="Your full professional biography..."
                            rows={10}
                          />
                        </div>

                        <div>
                          <Label>Specialties (comma-separated)</Label>
                          <Input 
                            value={bioForm.specialties?.join(', ') || ''}
                            onChange={(e) => setBioForm(prev => ({ 
                              ...prev, 
                              specialties: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                            }))}
                            placeholder="e.g., Executive Coaching, Leadership Development, Career Transition"
                          />
                        </div>

                        <div>
                          <Label>Credentials (comma-separated)</Label>
                          <Input 
                            value={bioForm.credentials?.join(', ') || ''}
                            onChange={(e) => setBioForm(prev => ({ 
                              ...prev, 
                              credentials: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                            }))}
                            placeholder="e.g., PCC, MBA, PhD"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button 
                            onClick={handleSaveBio}
                            disabled={savingProfile}
                            className="bg-forest hover:bg-forest/90"
                          >
                            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Save Profile
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setEditingBio(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label className="text-gray-500">Bio</Label>
                          <p className="text-gray-700 mt-1">{myProfile.coach?.bio || 'No bio set'}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                          <div>
                            <Label className="text-gray-500">Specialties</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(myProfile.coach?.specialties || []).map((s, i) => (
                                <Badge key={i} variant="secondary">{s}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="text-gray-500">Credentials</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(myProfile.coach?.credentials || []).map((c, i) => (
                                <Badge key={i} variant="outline">{c}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* My Clients */}
                <Card className="card-base lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-forest flex items-center gap-2">
                      <Users className="h-5 w-5" /> My Clients ({myProfile.clients?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {myProfile.clients?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No clients assigned yet</p>
                    ) : (
                      <div className="space-y-3">
                        {myProfile.clients?.map(client => (
                          <div 
                            key={client.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                                <User className="h-5 w-5 text-gold" />
                              </div>
                              <div>
                                <p className="font-medium">{client.user?.full_name || 'Unknown'}</p>
                                <p className="text-sm text-gray-500">{client.user?.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                client.status === 'active' ? 'bg-green-100 text-green-700' :
                                client.status === 'complete' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }>
                                {client.status}
                              </Badge>
                              {client.status === 'active' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleMarkClientComplete(client.user_id)}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Complete
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* My Agreements */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="text-forest flex items-center gap-2">
                      <FileText className="h-5 w-5" /> My Agreements ({myProfile.agreements?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {myProfile.agreements?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No agreements yet</p>
                    ) : (
                      <div className="space-y-2">
                        {myProfile.agreements?.map(agreement => (
                          <div 
                            key={agreement.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div>
                              <p className="font-medium text-sm">{agreement.title}</p>
                              <p className="text-xs text-gray-500">
                                {agreement.is_archived ? 'Archived' : agreement.status}
                              </p>
                            </div>
                            {agreement.document_url && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => window.open(agreement.document_url, '_blank')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Profile not available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <CoachSettingsTab />
          </TabsContent>
        </Tabs>

        {/* Add Note Dialog */}
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Coaching Note</DialogTitle>
              <DialogDescription>This note is only visible to you and administrators.</DialogDescription>
            </DialogHeader>
            
            {/* Coaching Notes Guidance */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <h4 className="font-semibold text-amber-800 mb-2">Coaching Notes Guidance</h4>
              <p className="text-amber-700 mb-2">
                Use this section for brief, high-level documentation only. Notes are for session verification, invoicing, and general progress tracking.
              </p>
              <p className="text-amber-700 font-medium mb-1">Include:</p>
              <ul className="text-amber-700 list-disc list-inside mb-2 space-y-0.5">
                <li>Date</li>
                <li>Duration</li>
                <li>General focus</li>
                <li>Agreed action steps</li>
                <li>Next session direction</li>
              </ul>
              <p className="text-amber-700 mb-2">Keep entries concise (2–5 sentences).</p>
              <p className="text-amber-800 font-medium">
                Do not include sensitive personal, medical, trauma, legal, or diagnostic information.
              </p>
              <p className="text-amber-700 italic mt-2">
                When in doubt, document less. High-level and strategic is best.
              </p>
            </div>

            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Client</label>
                <Select value={noteForm.client_id} onValueChange={(v) => setNoteForm({...noteForm, client_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id || client.user_id} value={client.id || client.user_id}>
                        {client.name || client.user_name} ({client.email || client.user_email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Note Type</label>
                <Select value={noteForm.note_type} onValueChange={(v) => setNoteForm({...noteForm, note_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="session">Session Notes</SelectItem>
                    <SelectItem value="initial_alignment">Initial Alignment Meeting</SelectItem>
                    <SelectItem value="midpoint_alignment">Midpoint Alignment Meeting</SelectItem>
                    <SelectItem value="final_alignment">Final Alignment Meeting</SelectItem>
                    <SelectItem value="360_assessment">360 Assessment & Feedback Session</SelectItem>
                    <SelectItem value="concern">Concern</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Note</label>
                <Textarea 
                  value={noteForm.note}
                  onChange={(e) => setNoteForm({...noteForm, note: e.target.value})}
                  placeholder="Date, duration, focus, action steps, next session direction..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleAddNote}>Add Note</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Invoice Dialog */}
        <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Invoice</DialogTitle>
              <DialogDescription>Admin will be notified of this invoice submission.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Invoice Number *</label>
                <Input 
                  value={invoiceForm.invoice_number}
                  onChange={(e) => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})}
                  placeholder="e.g., INV-2025-001"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Client *</label>
                <Select value={invoiceForm.client_id} onValueChange={(v) => setInvoiceForm({...invoiceForm, client_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id || client.user_id} value={client.id || client.user_id}>
                        {client.name || client.user_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Session Type *</label>
                <Select value={invoiceForm.session_type} onValueChange={(v) => setInvoiceForm({...invoiceForm, session_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select session type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="session">Coaching Session</SelectItem>
                    <SelectItem value="initial_alignment">Initial Alignment Meeting</SelectItem>
                    <SelectItem value="midpoint_alignment">Midpoint Alignment Meeting</SelectItem>
                    <SelectItem value="final_alignment">Final Alignment Meeting</SelectItem>
                    <SelectItem value="360_assessment">360 Assessment & Feedback Session</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {invoiceForm.session_type === 'session' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Session Numbers</label>
                  <Input 
                    value={invoiceForm.session_numbers}
                    onChange={(e) => setInvoiceForm({...invoiceForm, session_numbers: e.target.value})}
                    placeholder="1, 2, 3, 4"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter session numbers separated by commas</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Amount ($) *</label>
                <Input 
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm({...invoiceForm, amount: e.target.value})}
                  placeholder="600.00"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Session Dates (comma-separated)</label>
                <Input 
                  value={invoiceForm.session_dates}
                  onChange={(e) => setInvoiceForm({...invoiceForm, session_dates: e.target.value})}
                  placeholder="Jan 15, Jan 22, Jan 29"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Invoice Link <span className="text-red-500">*</span></label>
                <Input 
                  value={invoiceForm.invoice_link}
                  onChange={(e) => setInvoiceForm({...invoiceForm, invoice_link: e.target.value})}
                  placeholder="https://your-invoice-payment-link.com"
                  data-testid="invoice-link-input"
                />
                <p className="text-xs text-gray-500 mt-1">Required — this is the link Dr. Nicole will use to pay your invoice.</p>
              </div>
              <div className="flex items-start gap-3 p-4 bg-sand/30 rounded-lg">
                <input
                  type="checkbox"
                  id="notes_completed"
                  checked={invoiceForm.notes_completed}
                  onChange={(e) => setInvoiceForm({...invoiceForm, notes_completed: e.target.checked})}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-forest focus:ring-forest"
                />
                <label htmlFor="notes_completed" className="text-sm text-gray-700 cursor-pointer">
                  <span className="font-medium">Have you completed session notes for all billable hours in this invoice?</span>
                  <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Attach Invoice (PDF, JPG, PNG) <span className="text-red-500">*</span></label>
                <input 
                  id="invoice-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                />
                <label 
                  htmlFor="invoice-file-input" 
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-forest/30 rounded-lg cursor-pointer hover:border-forest hover:bg-forest/5 transition-colors text-sm text-forest font-medium"
                  data-testid="invoice-attach-button"
                >
                  <Paperclip className="h-4 w-4" />
                  {invoiceFile ? 'Change File' : 'Choose File to Attach'}
                </label>
                {invoiceFile ? (
                  <p className="text-xs text-green-600 mt-1" data-testid="invoice-file-selected">Selected: {invoiceFile.name}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">Required — upload a copy of your electronic invoice.</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowInvoiceDialog(false); setInvoiceFile(null); }}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSubmitInvoice} disabled={uploadingFile}>
                {uploadingFile ? 'Uploading...' : 'Submit Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ratings Dialog */}
        <Dialog open={showRatingsDialog} onOpenChange={setShowRatingsDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-forest">Ratings & Testimonials</DialogTitle>
              <DialogDescription>Your client feedback and reviews</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {/* Rating Summary */}
              <div className="text-center mb-6 p-4 bg-yellow-50 rounded-xl">
                <p className="text-5xl font-bold text-forest">{ratings.average_rating}</p>
                <div className="flex justify-center my-2">
                  {[1,2,3,4,5].map(i => (
                    <Star 
                      key={i} 
                      className={`h-6 w-6 ${i <= Math.round(ratings.average_rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-600">{ratings.total_reviews} total reviews</p>
              </div>
              
              {/* Reviews List */}
              {ratings.reviews.length === 0 ? (
                <div className="text-center py-8">
                  <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No reviews yet. Reviews from your clients will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ratings.reviews.map((review) => (
                    <div key={review.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-forest">{review.client_name}</p>
                          <div className="flex my-1">
                            {[1,2,3,4,5].map(i => (
                              <Star 
                                key={i} 
                                className={`h-4 w-4 ${i <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">{new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                      {review.testimonial && (
                        <p className="text-gray-700 italic">"{review.testimonial}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRatingsDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Preview Dialog */}
        <Dialog open={showInvoicePreview} onOpenChange={setShowInvoicePreview}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                Invoice Preview
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="py-4">
                <div className="border-b pb-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold text-forest">Invoice #{selectedInvoice.invoice_number}</h3>
                      <Badge className={getStatusBadge(selectedInvoice.status)}>{selectedInvoice.status}</Badge>
                      {selectedInvoice.is_overdue && (
                        <Badge className="ml-2 bg-red-500 text-white">OVERDUE</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-forest">${selectedInvoice.amount}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client:</span>
                    <span className="font-medium">{selectedInvoice.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sessions:</span>
                    <span className="font-medium">{selectedInvoice.sessions_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Session Dates:</span>
                    <span className="font-medium">{selectedInvoice.session_dates?.join(', ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium">{new Date(selectedInvoice.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Due Date:</span>
                    <span className={`font-medium ${selectedInvoice.is_overdue ? 'text-red-600' : ''}`}>
                      {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Terms:</span>
                    <span className="font-medium">{selectedInvoice.payment_terms || 'NET 60'}</span>
                  </div>
                  {selectedInvoice.invoice_link && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Invoice Link:</span>
                      <a 
                        href={selectedInvoice.invoice_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:underline truncate max-w-[220px]"
                        data-testid="invoice-preview-link"
                      >
                        {selectedInvoice.invoice_link}
                      </a>
                    </div>
                  )}
                </div>

                {selectedInvoice.notes && (
                  <div className="mt-4 p-3 bg-sand/30 rounded-lg">
                    <p className="text-sm text-gray-700"><strong>Notes:</strong> {selectedInvoice.notes}</p>
                  </div>
                )}

                {selectedInvoice.admin_comments && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700"><strong>Admin Feedback:</strong> {selectedInvoice.admin_comments}</p>
                  </div>
                )}

                {selectedInvoice.attachment_url && (
                  <div className="mt-4">
                    <a 
                      href={`${API_URL}${selectedInvoice.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                    >
                      <Paperclip className="h-4 w-4" />
                      View Attachment
                    </a>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvoicePreview(false)}>Close</Button>
              {selectedInvoice && ['pending', 'returned', 'rescinded'].includes(selectedInvoice.status) && (
                <Button 
                  className="btn-primary"
                  onClick={() => {
                    setShowInvoicePreview(false);
                    handleEditInvoice(selectedInvoice);
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" /> Edit Invoice
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={showEditInvoice} onOpenChange={setShowEditInvoice}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                Edit Invoice #{selectedInvoice?.invoice_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Number of Sessions</Label>
                <Input
                  type="number"
                  min="1"
                  value={editInvoiceForm.sessions_count}
                  onChange={(e) => setEditInvoiceForm({...editInvoiceForm, sessions_count: e.target.value})}
                  className="input-base"
                />
              </div>
              <div>
                <Label>Session Dates</Label>
                <Input
                  value={editInvoiceForm.session_dates}
                  onChange={(e) => setEditInvoiceForm({...editInvoiceForm, session_dates: e.target.value})}
                  placeholder="Jan 15, Jan 22, Jan 29"
                  className="input-base"
                />
              </div>
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editInvoiceForm.amount}
                  onChange={(e) => setEditInvoiceForm({...editInvoiceForm, amount: e.target.value})}
                  className="input-base"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editInvoiceForm.notes}
                  onChange={(e) => setEditInvoiceForm({...editInvoiceForm, notes: e.target.value})}
                  placeholder="Additional notes..."
                  className="input-base"
                />
              </div>
              
              {selectedInvoice?.status === 'returned' && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-700">
                    <strong>Returned by Admin:</strong> {selectedInvoice?.admin_comments || 'Please review and make necessary corrections.'}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    After saving, this invoice will be resubmitted for admin review.
                  </p>
                </div>
              )}
              
              {selectedInvoice?.status === 'rescinded' && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700">
                    <strong>Previously Rescinded:</strong> This invoice was rescinded. You can edit and resubmit it for review.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditInvoice(false)}>Cancel</Button>
              {['returned', 'rescinded'].includes(selectedInvoice?.status) ? (
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleSaveInvoiceEdit}>
                  <Send className="h-4 w-4 mr-2" /> Resubmit for Review
                </Button>
              ) : (
                <Button className="btn-primary" onClick={handleSaveInvoiceEdit}>
                  Save Changes
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CoachDashboardPage;
